import type { SessionAttendance, AttendanceStatus } from '../api/attendance.service';
import type { ClassSchedule, DayOfWeek } from '../api/class-schedule.service';
import type { ClassSession } from '../api/class-session.service';
import type { GroupEnrollment } from '../api/group-enrollment.service';
import type { Group } from '../api/group.service';
import type { Location } from '../api/location.service';
import type { DemoMember, DemoPaymentRecord, DemoStoreData, DemoTenant } from './store-types';

const DEMO_TENANT_ID = 'demo-tenant';
const OWNER_ID = 'demo-owner';

export const DEMO_OWNER_ID = OWNER_ID;
export const DEMO_TENANT_ID_CONST = DEMO_TENANT_ID;

const ROMANIAN_FIRST_NAMES = [
  'Andrei', 'Cristian', 'Luca', 'Matei', 'Darius', 'Vlad', 'Alex', 'Rareș',
  'Ioana', 'Maria', 'Elena', 'Ana', 'Sofia', 'Daria', 'Ilinca', 'Teodora',
];
const ROMANIAN_LAST_NAMES = [
  'Popescu', 'Ionescu', 'Popa', 'Stan', 'Marin', 'Dumitru', 'Radu', 'Dobre',
  'Moldovan', 'Constantin', 'Matei', 'Georgescu', 'Petrescu', 'Stoica', 'Neagu', 'Diaconu',
];

function uuid(prefix: string, i: number): string {
  return `${prefix}-${String(i).padStart(3, '0')}`;
}

function isoNow(): string {
  return new Date().toISOString();
}

function daysAgo(days: number): Date {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d;
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function dayOfWeekFromDate(d: Date): DayOfWeek {
  return (['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const)[d.getDay()];
}

export function buildDemoTenant(): DemoTenant {
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'https://demo.example.com';
  return {
    tenantId: DEMO_TENANT_ID,
    subdomain: 'demo',
    public: {
      tenantId: DEMO_TENANT_ID,
      subdomain: 'demo',
      teamName: 'Clubul Demo',
      discipline: 'Karate',
      status: 'Active',
      loginUrl: `${origin}/login`,
      countryCode: 'RO',
      locale: 'ro-RO',
      timezone: 'Europe/Bucharest',
      currency: 'RON',
      branding: {
        logoUrl: null,
        coverPhotoUrl: null,
        primaryColorHex: '#2E6BFF',
        accentColorHex: '#2E6BFF',
        hideSportDeskMentions: false,
      },
      terminologyByLocale: {
        'ro-RO': {
          coachSingular: 'Antrenor',
          coachPlural: 'Antrenori',
          athleteSingular: 'Sportiv',
          athletePlural: 'Sportivi',
          teamSingular: 'Echipă',
          teamPlural: 'Echipe',
          groupSingular: 'Grupă',
          groupPlural: 'Grupe',
          classSingular: 'Clasă',
          classPlural: 'Clase',
        },
      },
      publicSiteEnabled: true,
    },
  };
}

function buildMembers(): readonly DemoMember[] {
  const owner: DemoMember = {
    id: OWNER_ID,
    email: 'demo@sportdesk.ro',
    firstName: 'Alex',
    lastName: 'Demo',
    phone: '+40 700 000 000',
    roles: ['Owner'],
    status: 'Active',
    isClaimed: true,
    pending: false,
    createdAt: isoNow(),
    weightKg: null,
    heightCm: null,
    birthdate: null,
    gender: null,
    profilePictureUrl: null,
  };

  const coaches: DemoMember[] = [1, 2, 3].map((i) => ({
    id: uuid('coach', i),
    email: `antrenor${i}@demo.ro`,
    firstName: ROMANIAN_FIRST_NAMES[i - 1],
    lastName: ROMANIAN_LAST_NAMES[i - 1],
    phone: `+40 721 ${String(100 + i).padStart(3, '0')} ${String(200 + i).padStart(3, '0')}`,
    roles: ['Coach'],
    status: 'Active',
    isClaimed: true,
    pending: false,
    createdAt: isoNow(),
    weightKg: null,
    heightCm: null,
    birthdate: null,
    gender: null,
    profilePictureUrl: null,
  }));

  const athletes: DemoMember[] = [];
  for (let i = 0; i < 10; i++) {
    const first = ROMANIAN_FIRST_NAMES[(i + 3) % ROMANIAN_FIRST_NAMES.length];
    const last = ROMANIAN_LAST_NAMES[(i + 3) % ROMANIAN_LAST_NAMES.length];
    athletes.push({
      id: uuid('athlete', i + 1),
      email: i < 7 ? `${first.toLowerCase()}.${last.toLowerCase()}@demo.ro` : null,
      firstName: first,
      lastName: last,
      phone: `+40 732 ${String(300 + i).padStart(3, '0')} ${String(400 + i).padStart(3, '0')}`,
      roles: ['Student'],
      status: i === 9 ? 'Inactive' : 'Active',
      isClaimed: i < 7,
      pending: i >= 7,
      createdAt: isoNow(),
      weightKg: 35 + i * 3,
      heightCm: 140 + i * 5,
      birthdate: `${2010 + (i % 6)}-0${(i % 9) + 1}-1${i % 9}`,
      gender: i % 2 === 0 ? 'Male' : 'Female',
      profilePictureUrl: null,
    });
  }

  return [owner, ...coaches, ...athletes];
}

function buildLocations(): readonly Location[] {
  const now = isoNow();
  return [
    {
      locationId: 'loc-001',
      tenantId: DEMO_TENANT_ID,
      name: 'Sala Centrală',
      description: 'Sala principală de antrenament',
      address: {
        street: 'Strada Sportului 12',
        city: 'București',
        county: 'București',
        postalCode: '010101',
        countryCode: 'RO',
      },
      phone: '+40 213 111 222',
      email: 'centrala@demo.ro',
      capacity: 40,
      notes: null,
      status: 'Active',
      createdAt: now,
      updatedAt: null,
    },
    {
      locationId: 'loc-002',
      tenantId: DEMO_TENANT_ID,
      name: 'Sala Tineretului',
      description: 'Sala secundară',
      address: null,
      phone: null,
      email: null,
      capacity: 20,
      notes: 'Folosită pentru juniori',
      status: 'Active',
      createdAt: now,
      updatedAt: null,
    },
  ];
}

function buildGroups(coachIds: readonly string[]): readonly Group[] {
  const now = isoNow();
  return [
    {
      groupId: 'grp-001',
      tenantId: DEMO_TENANT_ID,
      name: 'Juniori',
      description: 'Grupa începători 8-12 ani',
      color: '#2E6BFF',
      minAge: 8,
      maxAge: 12,
      skillLevel: 'Beginner',
      defaultCoachTenantAccountIds: [coachIds[0]],
      status: 'Active',
      notes: null,
      monthlyFee: 200,
      createdAt: now,
      updatedAt: null,
    },
    {
      groupId: 'grp-002',
      tenantId: DEMO_TENANT_ID,
      name: 'Intermediari',
      description: 'Grupa 13-16 ani',
      color: '#16A34A',
      minAge: 13,
      maxAge: 16,
      skillLevel: 'Intermediate',
      defaultCoachTenantAccountIds: [coachIds[1]],
      status: 'Active',
      notes: null,
      monthlyFee: 250,
      createdAt: now,
      updatedAt: null,
    },
    {
      groupId: 'grp-003',
      tenantId: DEMO_TENANT_ID,
      name: 'Avansați',
      description: 'Grupa performanță',
      color: '#DC2626',
      minAge: 15,
      maxAge: null,
      skillLevel: 'Advanced',
      defaultCoachTenantAccountIds: [coachIds[2]],
      status: 'Active',
      notes: null,
      monthlyFee: 300,
      createdAt: now,
      updatedAt: null,
    },
  ];
}

function buildEnrollments(
  athletes: readonly DemoMember[],
  groups: readonly Group[],
): readonly GroupEnrollment[] {
  const now = isoNow();
  const list: GroupEnrollment[] = [];
  athletes.forEach((athlete, i) => {
    const group = groups[i % groups.length];
    list.push({
      enrollmentId: `enr-${String(i + 1).padStart(3, '0')}`,
      tenantId: DEMO_TENANT_ID,
      groupId: group.groupId,
      studentTenantAccountId: athlete.id,
      status: athlete.status === 'Active' ? 'Active' : 'Withdrawn',
      startDate: isoDate(daysAgo(60)),
      endDate: null,
      notes: null,
      monthlyFeeOverride: null,
      createdAt: now,
      updatedAt: null,
    });
  });
  return list;
}

function buildSchedules(
  coachIds: readonly string[],
  groups: readonly Group[],
  locations: readonly Location[],
): readonly ClassSchedule[] {
  const now = isoNow();
  const effectiveFrom = isoDate(daysAgo(90));
  const base: Array<Pick<ClassSchedule, 'dayOfWeek' | 'startTime' | 'endTime'> & {
    group: number;
    coach: number;
    location: number;
  }> = [
    { dayOfWeek: 'Monday', startTime: '17:00', endTime: '18:00', group: 0, coach: 0, location: 0 },
    { dayOfWeek: 'Wednesday', startTime: '17:00', endTime: '18:00', group: 0, coach: 0, location: 0 },
    { dayOfWeek: 'Tuesday', startTime: '18:30', endTime: '20:00', group: 1, coach: 1, location: 0 },
    { dayOfWeek: 'Thursday', startTime: '18:30', endTime: '20:00', group: 1, coach: 1, location: 0 },
    { dayOfWeek: 'Saturday', startTime: '10:00', endTime: '12:00', group: 2, coach: 2, location: 1 },
  ];
  return base.map((item, i) => ({
    scheduleId: `sched-${String(i + 1).padStart(3, '0')}`,
    tenantId: DEMO_TENANT_ID,
    groupId: groups[item.group].groupId,
    locationId: locations[item.location].locationId,
    coachTenantAccountIds: [coachIds[item.coach]],
    dayOfWeek: item.dayOfWeek,
    startTime: item.startTime,
    endTime: item.endTime,
    effectiveFrom,
    effectiveUntil: null,
    title: null,
    notes: null,
    status: 'Active',
    createdAt: now,
    updatedAt: null,
  }));
}

function buildSessionsFromSchedules(
  schedules: readonly ClassSchedule[],
  daysPast: number,
  daysFuture: number,
): readonly ClassSession[] {
  const list: ClassSession[] = [];
  const scheduleByDay = new Map<DayOfWeek, ClassSchedule[]>();
  for (const s of schedules) {
    const arr = scheduleByDay.get(s.dayOfWeek) ?? [];
    arr.push(s);
    scheduleByDay.set(s.dayOfWeek, arr);
  }
  for (let d = -daysPast; d <= daysFuture; d++) {
    const date = daysAgo(-d);
    const dow = dayOfWeekFromDate(date);
    const same = scheduleByDay.get(dow) ?? [];
    for (const s of same) {
      const dateStr = isoDate(date);
      list.push({
        sessionId: `sess-${dateStr}-${s.scheduleId}`,
        tenantId: s.tenantId,
        classScheduleId: s.scheduleId,
        groupId: s.groupId,
        locationId: s.locationId,
        coachTenantAccountIds: s.coachTenantAccountIds,
        date: dateStr,
        startTime: s.startTime,
        endTime: s.endTime,
        status: d < 0 ? 'Completed' : 'Scheduled',
        type: 'Regular',
        title: null,
        notes: null,
        canceledAt: null,
        cancelReason: null,
        createdAt: isoNow(),
        updatedAt: null,
      });
    }
  }
  return list;
}

function buildAttendances(
  sessions: readonly ClassSession[],
  enrollments: readonly GroupEnrollment[],
): readonly SessionAttendance[] {
  const now = isoNow();
  const list: SessionAttendance[] = [];
  for (const session of sessions) {
    if (session.status !== 'Completed') continue;
    const members = enrollments.filter(
      (e) => e.groupId === session.groupId && e.status === 'Active',
    );
    members.forEach((e, i) => {
      const statuses: AttendanceStatus[] = ['Present', 'Present', 'Present', 'Late', 'Absent'];
      list.push({
        attendanceId: `att-${session.sessionId}-${e.studentTenantAccountId}`,
        classSessionId: session.sessionId,
        studentTenantAccountId: e.studentTenantAccountId,
        status: statuses[i % statuses.length],
        notes: null,
        isGuest: false,
        checkedInAt: `${session.date}T${session.startTime}:00`,
        checkedInByTenantAccountId: session.coachTenantAccountIds[0] ?? null,
        createdAt: now,
        updatedAt: null,
      });
    });
  }
  return list;
}

function buildPayments(
  athletes: readonly DemoMember[],
  enrollments: readonly GroupEnrollment[],
  groups: readonly Group[],
): readonly DemoPaymentRecord[] {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const list: DemoPaymentRecord[] = [];
  athletes.forEach((athlete, i) => {
    const enr = enrollments.find((e) => e.studentTenantAccountId === athlete.id);
    const group = enr ? groups.find((g) => g.groupId === enr.groupId) : null;
    const fee = group?.monthlyFee ?? 200;
    const paid = i % 3 === 0;
    const waived = i === 5;
    list.push({
      paymentId: `pay-${year}-${month}-${athlete.id}`,
      studentTenantAccountId: athlete.id,
      year,
      month,
      status: waived ? 'Waived' : paid ? 'Paid' : 'Unpaid',
      amountDue: fee,
      amountPaid: paid ? fee : null,
      paidAt: paid ? isoNow() : null,
      paidMethod: paid ? 'Cash' : null,
      note: null,
      waivedReason: waived ? 'Bursă de merit' : null,
    });
  });
  return list;
}

export function buildSeedData(): DemoStoreData {
  const tenant = buildDemoTenant();
  const members = buildMembers();
  const coachIds = members.filter((m) => m.roles.includes('Coach')).map((m) => m.id);
  const athletes = members.filter((m) => m.roles.includes('Student'));
  const locations = buildLocations();
  const groups = buildGroups(coachIds);
  const enrollments = buildEnrollments(athletes, groups);
  const schedules = buildSchedules(coachIds, groups, locations);
  const sessions = buildSessionsFromSchedules(schedules, 14, 14);
  const attendances = buildAttendances(sessions, enrollments);
  const payments = buildPayments(athletes, enrollments, groups);

  return {
    version: 1,
    tenant,
    members,
    locations,
    groups,
    enrollments,
    schedules,
    sessions,
    attendances,
    payments,
    events: [],
    announcements: [],
    currentMemberId: OWNER_ID,
  };
}
